import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Ban, Search, Clock, Users, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import EmployeeCombobox from '@/pages/payroll/components/EmployeeCombobox';
import { fetchPersonalEmpleados } from '../Services/personal.services';
import {
  getAusencias,
  crearAusencia,
  actualizarAusencia,
  anularAusencia,
} from './Services/ausencias.services';

const MOTIVOS = {
  incapacidad: [
    'Enfermedad general',
    'Accidente de trabajo',
    'Enfermedad laboral',
    'Licencia de maternidad',
    'Licencia de paternidad',
    'Otro',
  ],
  permiso: [
    'Cita médica',
    'Calamidad doméstica',
    'Licencia de luto',
    'Diligencia personal',
    'Estudio',
    'Otro',
  ],
};

const TEXTOS = {
  incapacidad: {
    singular: 'incapacidad',
    nueva: 'Registrar incapacidad',
    descripcion:
      'La incapacidad entra a la nómina del periodo con el porcentaje configurado en Recargos y multiplicadores.',
  },
  permiso: {
    singular: 'permiso',
    nuevo: 'Registrar permiso',
    descripcion:
      'Los permisos remunerados se pagan como hora ordinaria; los no remunerados se muestran en la nómina sin valor.',
  },
};

const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

const primerDiaMes = () => `${hoyISO().slice(0, 7)}-01`;

const formatear = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(a, m - 1, d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const diasRango = (desde, hasta) =>
  Math.round((Date.parse(`${hasta}T12:00:00Z`) - Date.parse(`${desde}T12:00:00Z`)) / 86400000) + 1;

const formVacio = (tipo) => ({
  empleado: null,
  tipo,
  origen: 'comun',
  remunerado: true,
  motivo: MOTIVOS[tipo][0],
  fechaInicio: hoyISO(),
  fechaFin: hoyISO(),
  horasPorDia: '',
  observaciones: '',
});

/**
 * Listado y registro de incapacidades o permisos (mismo componente con `tipo`).
 * Con `centro` ({ id, nombre }) se gestiona desde el dashboard del centro de lavado:
 * lista las ausencias del centro y su personal, y registra con esa sede.
 */
const AusenciasPage = ({ tipo, centro = null }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const autor = { id: user?._id || user?.id || undefined, nombre: user?.nombre || user?.name || user?.email || undefined };
  const textos = TEXTOS[tipo];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empleados, setEmpleados] = useState([]);
  const [otrosCentros, setOtrosCentros] = useState(false);
  const [filtros, setFiltros] = useState({ desde: primerDiaMes(), hasta: '', estado: 'activa', texto: '' });
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(formVacio(tipo));
  const [guardando, setGuardando] = useState(false);
  const [aAnular, setAAnular] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const lista = await getAusencias({
        tipo,
        desde: filtros.desde || undefined,
        hasta: filtros.hasta || undefined,
        estado: filtros.estado,
        lavanderiaId: centro?.id || undefined,
      });
      setItems(lista);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [tipo, centro?.id, filtros.desde, filtros.hasta, filtros.estado]);

  useEffect(() => {
    fetchPersonalEmpleados({ limit: 1000, query: { estado: 'Activo' } })
      .then((r) => setEmpleados(r.data))
      .catch(() => setEmpleados([]));
  }, []);

  // En un centro se ofrece primero su personal; se puede abrir a todos (apoyos de otra sede).
  const empleadosElegibles = useMemo(() => {
    if (!centro || otrosCentros) return empleados;
    return empleados.filter((e) => String(e.lavanderia?.id || e.lavanderia?._id || '') === String(centro.id));
  }, [empleados, centro, otrosCentros]);

  const visibles = useMemo(() => {
    const q = filtros.texto.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      `${i.empleado?.nombre || ''} ${i.empleado?.cedula || ''} ${i.motivo || ''}`.toLowerCase().includes(q),
    );
  }, [items, filtros.texto]);

  const resumen = useMemo(() => {
    const activas = visibles.filter((i) => i.estado === 'activa');
    return {
      registros: activas.length,
      personas: new Set(activas.map((i) => i.empleado?.id)).size,
      dias: activas.reduce((s, i) => s + diasRango(i.fechaInicio, i.fechaFin), 0),
    };
  }, [visibles]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(formVacio(tipo));
    setOpen(true);
  };

  const abrirEdicion = (item) => {
    setEditando(item);
    setForm({
      empleado: item.empleado,
      tipo,
      origen: item.origen || 'comun',
      remunerado: item.remunerado !== false,
      motivo: item.motivo || MOTIVOS[tipo][0],
      fechaInicio: item.fechaInicio,
      fechaFin: item.fechaFin,
      horasPorDia: item.horasPorDia ?? '',
      observaciones: item.observaciones || '',
    });
    setOpen(true);
  };

  const elegirEmpleado = (emp) => {
    if (!emp) return setForm((f) => ({ ...f, empleado: null }));
    setForm((f) => ({
      ...f,
      empleado: {
        id: emp._id,
        nombre: `${emp.nombre || ''} ${emp.apellido || ''}`.trim(),
        cedula: emp.cedula,
        rol: emp.rol,
      },
      lavanderia: centro
        ? { id: centro.id, nombre: centro.nombre }
        : emp.lavanderia
          ? { id: emp.lavanderia.id || emp.lavanderia._id, nombre: emp.lavanderia.nombre }
          : null,
    }));
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.empleado) {
      toast({ title: 'Faltan datos', description: 'Selecciona la persona.', variant: 'destructive' });
      return;
    }
    if (form.fechaFin < form.fechaInicio) {
      toast({ title: 'Fechas', description: 'La fecha final es anterior a la inicial.', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    try {
      const payload = {
        ...form,
        horasPorDia: form.horasPorDia === '' ? null : Number(form.horasPorDia),
      };
      if (editando) {
        await actualizarAusencia(editando._id, payload);
        toast({ title: 'Guardado', description: `Se actualizó ${textos.singular === 'incapacidad' ? 'la incapacidad' : 'el permiso'}.` });
      } else {
        await crearAusencia({ ...payload, creadoPor: autor });
        toast({ title: 'Guardado', description: `Se registró ${textos.singular === 'incapacidad' ? 'la incapacidad' : 'el permiso'}.` });
      }
      setOpen(false);
      cargar();
    } catch (err) {
      toast({ title: 'No se guardó', description: err.message, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const confirmarAnular = async () => {
    try {
      await anularAusencia(aAnular._id, { anuladoPor: autor, motivo: motivoAnulacion });
      toast({ title: 'Anulado', description: 'El registro ya no se tendrá en cuenta en la nómina.' });
      setAAnular(null);
      setMotivoAnulacion('');
      cargar();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const esIncapacidad = tipo === 'incapacidad';

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={filtros.estado} onValueChange={(v) => setFiltros({ ...filtros, estado: v })}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activa">Activos</SelectItem>
                <SelectItem value="anulada">Anulados</SelectItem>
                <SelectItem value="todas">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-xs">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={filtros.texto}
                onChange={(e) => setFiltros({ ...filtros, texto: e.target.value })}
                placeholder="Nombre, cédula o motivo"
                className="pl-8"
              />
            </div>
          </div>
          <Button onClick={abrirNuevo}>
            <Plus className="mr-2 h-4 w-4" /> {esIncapacidad ? textos.nueva : textos.nuevo}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <CardStat icon={CalendarDays} titulo="Registros" valor={loading ? '—' : resumen.registros} descripcion="Activos en el filtro" />
          <CardStat icon={Users} titulo="Personas" valor={loading ? '—' : resumen.personas} descripcion="Con al menos un registro" />
          <CardStat icon={Clock} titulo="Días" valor={loading ? '—' : resumen.dias} descripcion="Días calendario sumados" />
        </div>

        <div className="rounded-lg border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Persona</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead className="text-right">Días</TableHead>
                <TableHead>{esIncapacidad ? 'Origen' : 'Pago'}</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Horas por día</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">Cargando...</TableCell>
                </TableRow>
              ) : visibles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    No hay registros en este filtro.
                  </TableCell>
                </TableRow>
              ) : (
                visibles.map((item) => (
                  <TableRow key={item._id} className={item.estado === 'anulada' ? 'opacity-60' : ''}>
                    <TableCell>
                      <div className="font-medium">{item.empleado?.nombre}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.empleado?.cedula || 'Sin cédula'}
                        {item.lavanderia?.nombre ? ` · ${item.lavanderia.nombre}` : ''}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatear(item.fechaInicio)}
                      {item.fechaFin !== item.fechaInicio ? ` – ${formatear(item.fechaFin)}` : ''}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{diasRango(item.fechaInicio, item.fechaFin)}</TableCell>
                    <TableCell>
                      {esIncapacidad ? (
                        <Badge variant="outline">{item.origen === 'laboral' ? 'Laboral' : 'Común'}</Badge>
                      ) : item.remunerado ? (
                        <Badge variant="outline">Remunerado</Badge>
                      ) : (
                        <Badge variant="secondary">No remunerado</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.motivo || '—'}
                      {item.observaciones ? <div className="text-xs line-clamp-1">{item.observaciones}</div> : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.horasPorDia != null ? `${item.horasPorDia} h` : 'Según turno'}
                    </TableCell>
                    <TableCell>
                      {item.estado === 'anulada' ? (
                        <Badge variant="destructive" title={item.anuladoPor?.motivo || ''}>Anulado</Badge>
                      ) : (
                        <Badge>Activo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {item.estado === 'activa' && (
                        <>
                          <Button variant="ghost" size="icon" title="Editar" onClick={() => abrirEdicion(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Anular" className="text-destructive" onClick={() => setAAnular(item)}>
                            <Ban className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? `Editar ${textos.singular}` : esIncapacidad ? textos.nueva : textos.nuevo}</DialogTitle>
            <DialogDescription>{textos.descripcion}</DialogDescription>
          </DialogHeader>
          <form onSubmit={guardar} className="space-y-4">
            <div className="space-y-2">
              <Label>Persona</Label>
              {editando ? (
                <div className="rounded-md border px-3 py-2 text-sm">{form.empleado?.nombre}</div>
              ) : (
                <>
                  <EmployeeCombobox employees={empleadosElegibles} value={form.empleado?.id || null} onValueChange={elegirEmpleado} />
                  {centro && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <input type="checkbox" checked={otrosCentros} onChange={(e) => setOtrosCentros(e.target.checked)} />
                      Incluir personal de otros centros ({empleadosElegibles.length} disponibles)
                    </label>
                  )}
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="aus-desde">Desde</Label>
                <Input id="aus-desde" type="date" value={form.fechaInicio} onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aus-hasta">Hasta</Label>
                <Input id="aus-hasta" type="date" value={form.fechaFin} min={form.fechaInicio} onChange={(e) => setForm({ ...form, fechaFin: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {esIncapacidad ? (
                <div className="space-y-2">
                  <Label>Origen</Label>
                  <Select value={form.origen} onValueChange={(v) => setForm({ ...form, origen: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comun">Común</SelectItem>
                      <SelectItem value="laboral">Laboral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Pago</Label>
                  <Select value={form.remunerado ? 'si' : 'no'} onValueChange={(v) => setForm({ ...form, remunerado: v === 'si' })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="si">Remunerado</SelectItem>
                      <SelectItem value="no">No remunerado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Select value={form.motivo} onValueChange={(v) => setForm({ ...form, motivo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOTIVOS[tipo].map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="aus-horas">Horas por día (opcional)</Label>
              <Input
                id="aus-horas"
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={form.horasPorDia}
                onChange={(e) => setForm({ ...form, horasPorDia: e.target.value })}
                placeholder="Vacío = horas del turno planificado de cada día"
              />
              <p className="text-xs text-muted-foreground">
                Si se deja vacío se toman las horas planificadas de cada día; sin planificación, la jornada diaria de los
                parámetros de nómina (domingos y festivos sin turno no suman).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="aus-obs">Observaciones</Label>
              <Textarea id="aus-obs" rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!aAnular} onOpenChange={(v) => !v && setAAnular(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anular {textos.singular}</DialogTitle>
            <DialogDescription>
              {aAnular?.empleado?.nombre}, {formatear(aAnular?.fechaInicio)} – {formatear(aAnular?.fechaFin)}. Queda en el
              historial pero deja de contar en las nóminas que se recalculen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="aus-motivo-anul">Motivo</Label>
            <Input id="aus-motivo-anul" value={motivoAnulacion} onChange={(e) => setMotivoAnulacion(e.target.value)} placeholder="Ej. registrado por error" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAAnular(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarAnular}>Anular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

const CardStat = ({ icon: Icon, titulo, valor, descripcion }) => (
  <div className="rounded-lg border bg-card p-4">
    <div className="flex items-center gap-2">
      {Icon && (
        <div className="p-1.5 rounded-md bg-primary/15">
          <Icon className="h-4 w-4 text-primary" />
        </div>
      )}
      <span className="text-sm text-muted-foreground">{titulo}</span>
    </div>
    <div className="text-2xl font-bold mt-1">{valor}</div>
    <p className="text-xs text-muted-foreground">{descripcion}</p>
  </div>
);

export default AusenciasPage;
